import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Package, 
  MapPin, 
  Calendar, 
  CheckCircle2, 
  Circle, 
  Loader2,
  AlertCircle,
  FileCheck,
  Camera,
  Image
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState } from "react";
import { format } from "date-fns";
import { fi } from "date-fns/locale";

const statusColors: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  confirmed: "bg-blue-100 text-blue-800",
  ready_for_installation: "bg-amber-100 text-amber-800",
  installed: "bg-green-100 text-green-800",
  completed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

const fulfillmentLabels: Record<string, string> = {
  delivery: "Toimitus",
  installation: "Asennus",
  pickup: "Nouto",
  return: "Palautus",
};

export default function OrderTracking() {
  const { orderNumber, token } = useParams<{ orderNumber: string; token: string }>();
  const [previewPhoto, setPreviewPhoto] = useState<{
    id: string;
    url?: string;
    photo_type: string;
    captured_at?: string;
    latitude?: number;
    longitude?: number;
    notes?: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["order-tracking", orderNumber, token],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-order-tracking?order_number=${encodeURIComponent(orderNumber!)}&token=${encodeURIComponent(token!)}`,
        {
          headers: {
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Tilausta ei löytynyt");
      }
      
      return response.json();
    },
    enabled: !!orderNumber && !!token,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Ladataan tilausta...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Tilausta ei löytynyt</h2>
            <p className="text-muted-foreground">
              Tarkista, että linkki on oikein tai ota yhteyttä asiakaspalveluun.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { order, timeline, proofApproved, photos } = data;

  type TrackingPhoto = { id: string; url?: string; photo_type: string; captured_at?: string; latitude?: number; longitude?: number; notes?: string };
  const beforePhotos = (photos as TrackingPhoto[] | undefined)?.filter((p) => p.photo_type === 'before') || [];
  const afterPhotos = (photos as TrackingPhoto[] | undefined)?.filter((p) => p.photo_type === 'after') || [];

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold">Tilauksen seuranta</h1>
          <p className="text-muted-foreground mt-1">Tilausnumero: {order.order_number}</p>
        </div>

        {/* Status Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Tilauksen tila</CardTitle>
              <Badge className={statusColors[order.status as string] || "bg-muted"}>
                {(timeline as Array<{ status: string; current?: boolean; completed?: boolean; label?: { fi: string }; date?: string }>).find((t) => t.current)?.label?.fi || order.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {/* Timeline */}
            <div className="space-y-4">
              {(timeline as Array<{ status: string; current?: boolean; completed?: boolean; label?: { fi: string }; date?: string }>).map((step, index: number) => (
                <div key={step.status} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    {step.completed ? (
                      <CheckCircle2 className={`h-6 w-6 ${step.current ? 'text-primary' : 'text-green-500'}`} />
                    ) : (
                      <Circle className="h-6 w-6 text-muted-foreground" />
                    )}
                    {index < timeline.length - 1 && (
                      <div className={`w-0.5 h-8 mt-1 ${step.completed ? 'bg-green-500' : 'bg-muted'}`} />
                    )}
                  </div>
                  <div className="flex-1 pb-2">
                    <p className={`font-medium ${step.completed ? '' : 'text-muted-foreground'}`}>
                      {step.label?.fi}
                    </p>
                    {step.date && (
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(step.date), "d.M.yyyy", { locale: fi })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Order Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5" />
              Tilauksen tiedot
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Tilauspäivä</p>
                <p className="font-medium">
                  {format(new Date(order.order_date), "d.M.yyyy", { locale: fi })}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Toimitustapa</p>
                <p className="font-medium">
                  {fulfillmentLabels[order.fulfillment_type] || order.fulfillment_type}
                </p>
              </div>
              {order.installation_date && (
                <div>
                  <p className="text-muted-foreground">Arvioitu asennuspäivä</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(order.installation_date), "d.M.yyyy", { locale: fi })}
                  </p>
                </div>
              )}
              {order.delivery_date && (
                <div>
                  <p className="text-muted-foreground">Toimituspäivä</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(order.delivery_date), "d.M.yyyy", { locale: fi })}
                  </p>
                </div>
              )}
            </div>

            {order.cemetery && (
              <>
                <Separator />
                <div>
                  <p className="text-muted-foreground text-sm mb-1">Hautausmaa</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {order.cemetery.name}, {order.cemetery.city}
                  </p>
                </div>
              </>
            )}

            {order.items && order.items.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-muted-foreground text-sm mb-2">Tilauksen sisältö</p>
                  <ul className="space-y-1">
                    {(order.items as Array<{ description: string; quantity: number }>).map((item, index: number) => (
                      <li key={index} className="text-sm flex justify-between">
                        <span>{item.description}</span>
                        <span className="text-muted-foreground">{item.quantity} kpl</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {proofApproved && (
              <>
                <Separator />
                <div className="flex items-center gap-2 text-green-600">
                  <FileCheck className="h-5 w-5" />
                  <span className="text-sm font-medium">Vedos hyväksytty</span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Installation Photos */}
        {(beforePhotos.length > 0 || afterPhotos.length > 0) && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="h-5 w-5" />
                Asennuskuvat
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {beforePhotos.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2 flex items-center gap-1">
                    <Image className="h-4 w-4" />
                    Ennen ({beforePhotos.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {beforePhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className="aspect-square rounded-md overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setPreviewPhoto(photo)}
                      >
                        {photo.url ? (
                          <img
                            src={photo.url}
                            alt="Ennen"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {afterPhotos.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-2 flex items-center gap-1">
                    <Image className="h-4 w-4" />
                    Jälkeen ({afterPhotos.length})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {afterPhotos.map((photo) => (
                      <div
                        key={photo.id}
                        className="aspect-square rounded-md overflow-hidden bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setPreviewPhoto(photo)}
                      >
                        {photo.url ? (
                          <img
                            src={photo.url}
                            alt="Jälkeen"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Image className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground">
          Kysyttävää tilauksestasi? Ota yhteyttä asiakaspalveluumme.
        </p>
      </div>

      {/* Photo Preview Dialog */}
      <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {previewPhoto?.photo_type === 'before' ? 'Ennen' : 'Jälkeen'}
              {previewPhoto?.captured_at && (
                <span className="font-normal text-muted-foreground ml-2">
                  {format(new Date(previewPhoto.captured_at), "d.M.yyyy", { locale: fi })}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {previewPhoto?.url && (
            <div className="space-y-3">
              <img
                src={previewPhoto.url}
                alt={previewPhoto.photo_type === 'before' ? 'Ennen' : 'Jälkeen'}
                className="w-full max-h-[60vh] object-contain rounded-md"
              />
              {previewPhoto.latitude && previewPhoto.longitude && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <a
                    href={`https://www.google.com/maps?q=${previewPhoto.latitude},${previewPhoto.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Näytä sijainti kartalla
                  </a>
                </p>
              )}
              {previewPhoto.notes && (
                <p className="text-sm text-muted-foreground">{previewPhoto.notes}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
